import { useState, useCallback } from 'react';
import { useServices } from 'services';
import useLoadingStateManager from './useLoadingStateManager';
import { LoadingStatus } from 'types/LoadingStateManager';
import User from 'types/User';
import { MembershipTier } from 'types/SignUpData';
import { toBackendTier } from 'utils/tierMapping';

interface ResolveTierUpgradeParams {
  email: string;
  resolution: 'approved' | 'denied';
  /** The tier the admin is actually granting (may differ from originalRequestedTier). */
  assignedTier?: MembershipTier;
  /** The tier the user originally requested. */
  originalRequestedTier?: MembershipTier;
  comment?: string;
  eventTimestamp: string;
  orcidId?: string;
  currentUserTier?: string;
}

interface UseUserApprovalReturn {
  pendingUsers: User[];
  pendingUpgradeRequests: User[];
  resolvedUpgradeRequests: User[];
  recentlyReviewedUsers: User[];
  isLoading: boolean;
  fetchUsers: () => Promise<void>;
  approveUser: (user: User, comment?: string, assignedTier?: string) => Promise<void>;
  denyUser: (user: User, comment?: string) => Promise<void>;
  resolveTierUpgrade: (params: ResolveTierUpgradeParams) => Promise<void>;
}

export default function useUserApproval(): UseUserApprovalReturn {
  const [pendingUsers, setPendingUsers] = useState<User[]>([]);
  const [pendingUpgradeRequests, setPendingUpgradeRequests] = useState<User[]>([]);
  const [resolvedUpgradeRequests, setResolvedUpgradeRequests] = useState<User[]>([]);
  const [recentlyReviewedUsers, setRecentlyReviewedUsers] = useState<User[]>([]);

  const { authService } = useServices();
  const loadingStateManager = useLoadingStateManager();
  const isLoading = loadingStateManager.loadingState.status === LoadingStatus.LOADING;

  const fetchUsers = useCallback(async (): Promise<void> => {
    try {
      const [
        pendingUsersData,
        recentlyApprovedUsersData,
        pendingUpgradeData,
        resolvedUpgradeData,
      ] = await Promise.all([
        authService.fetchPendingApprovalUsers(),
        authService.fetchRecentlyApprovedUsers(),
        authService.fetchAllPendingUpgrades(),
        authService.fetchAllResolvedUpgradeRequests(),
      ]);

      setPendingUsers(pendingUsersData);
      setRecentlyReviewedUsers(recentlyApprovedUsersData);
      setPendingUpgradeRequests(pendingUpgradeData);
      setResolvedUpgradeRequests(resolvedUpgradeData);
    } catch (error) {
      console.error('Error fetching users:', error);
    }
  }, [authService]);

  const refreshUpgradeLists = useCallback(async (): Promise<void> => {
    const [pendingUpgradeData, resolvedUpgradeData] = await Promise.all([
      authService.fetchAllPendingUpgrades(),
      authService.fetchAllResolvedUpgradeRequests(),
    ]);
    setPendingUpgradeRequests(pendingUpgradeData);
    setResolvedUpgradeRequests(resolvedUpgradeData);
  }, [authService]);

  const approveUser = useCallback(
    async (user: User, comment?: string, assignedTier?: string): Promise<void> => {
      const refreshData = async (): Promise<void> => {
        await authService.approve_new_user(user, comment, assignedTier);
        // Optimistically remove from pending list immediately — Cognito's
        // list_users filter index has eventual consistency so a refetch right
        // after the attribute update often still returns the user as pending.
        setPendingUsers((prev) => prev.filter((u) => u.email !== user.email));
        // Background-sync to pick up any other changes
        const [updatedPendingUsers, updatedRecentlyApprovedUsers] = await Promise.all([
          authService.fetchPendingApprovalUsers(),
          authService.fetchRecentlyApprovedUsers(),
        ]);
        // Only apply the refetch result if the user is no longer in it
        // (guards against a still-stale response overwriting our optimistic update)
        setPendingUsers((prev) => {
          const stillPending = updatedPendingUsers.some((u) => u.email === user.email);
          return stillPending ? prev.filter((u) => u.email !== user.email) : updatedPendingUsers;
        });
        setRecentlyReviewedUsers(updatedRecentlyApprovedUsers);
      };
      await loadingStateManager.startLoading({ waitOn: refreshData() });
    },
    [authService, loadingStateManager]
  );

  const denyUser = useCallback(
    async (user: User, comment?: string): Promise<void> => {
      const refreshData = async (): Promise<void> => {
        await authService.deny_new_user(user, comment);
        // Optimistically remove from pending list immediately (see approveUser comment)
        setPendingUsers((prev) => prev.filter((u) => u.email !== user.email));
        const [updatedPendingUsers, updatedRecentlyApprovedUsers] = await Promise.all([
          authService.fetchPendingApprovalUsers(),
          authService.fetchRecentlyApprovedUsers(),
        ]);
        setPendingUsers((prev) => {
          const stillPending = updatedPendingUsers.some((u) => u.email === user.email);
          return stillPending ? prev.filter((u) => u.email !== user.email) : updatedPendingUsers;
        });
        setRecentlyReviewedUsers(updatedRecentlyApprovedUsers);
      };
      await loadingStateManager.startLoading({ waitOn: refreshData() });
    },
    [authService, loadingStateManager]
  );

  const resolveTierUpgrade = useCallback(
    async ({
      email,
      resolution,
      assignedTier,
      originalRequestedTier,
      comment,
      eventTimestamp,
      orcidId,
      currentUserTier,
    }: ResolveTierUpgradeParams): Promise<void> => {
      const doResolve = async (): Promise<void> => {
        await authService.rdmApiService.axios.post(
          `/upgrade-decision/${encodeURIComponent(email)}`,
          {
            decision: resolution.toUpperCase(),
            // new_tier: what the admin actually grants (may differ from the user's original request)
            new_tier: assignedTier,
            // requested_tier: what the user originally asked for
            requested_tier: originalRequestedTier ?? assignedTier,
            comments: comment,
            current_user_tier: currentUserTier,
            ...(orcidId ? { orcid_id: orcidId } : {}),
          },
          { headers: { 'X-CSRFToken': authService.csrfToken } }
        );
        // Optimistically remove from upgrade pending list before Cognito syncs
        setPendingUpgradeRequests((prev) => prev.filter((u) => u.email !== email));
        await refreshUpgradeLists();
        // Guard against stale refetch re-adding the resolved user
        setPendingUpgradeRequests((prev) => prev.filter((u) => u.email !== email));
      };
      await loadingStateManager.startLoading({ waitOn: doResolve() });
    },
    [authService, loadingStateManager, refreshUpgradeLists]
  );

  return {
    pendingUsers,
    pendingUpgradeRequests,
    resolvedUpgradeRequests,
    recentlyReviewedUsers,
    isLoading,
    fetchUsers,
    approveUser,
    denyUser,
    resolveTierUpgrade,
  };
}